import { useEffect, useState } from "react";
import { getAdminHeaders } from "./adminApi";

const emptyQuestion = {
  question_text: "",
  options: [
    { text: "", score: 1 },
    { text: "", score: 1 },
  ],
  sort_order: 0,
  is_enabled: 1,
};

export default function AdminQuestionnaire() {
  const [settingsForm, setSettingsForm] = useState({
    page_title: "",
    page_subtitle: "",
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyQuestion);
  const [message, setMessage] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, itemsRes] = await Promise.all([
        fetch("/api/admin/questionnaire/settings", { headers: getAdminHeaders() }),
        fetch("/api/admin/questionnaire/questions", { headers: getAdminHeaders() }),
      ]);
      const settingsData = await settingsRes.json();
      const itemsData = await itemsRes.json();
      if (settingsRes.ok) {
        setSettingsForm({
          page_title: settingsData.page_title || "",
          page_subtitle: settingsData.page_subtitle || "",
        });
      }
      if (itemsRes.ok) {
        setItems(itemsData.items || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    setMessage("");
    try {
      const res = await fetch("/api/admin/questionnaire/settings", {
        method: "PATCH",
        headers: getAdminHeaders(true),
        body: JSON.stringify(settingsForm),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "保存失败");
        return;
      }
      setSettingsForm({
        page_title: data.settings.page_title || "",
        page_subtitle: data.settings.page_subtitle || "",
      });
      setMessage("页面设置已保存");
    } finally {
      setSavingSettings(false);
    }
  };

  const openCreate = () => {
    const maxSort = items.reduce((max, item) => Math.max(max, item.sort_order || 0), 0);
    setForm({ ...emptyQuestion, sort_order: maxSort + 1 });
    setCreating(true);
    setEditing(null);
  };

  const openEdit = (item) => {
    setForm({
      question_text: item.q,
      options: item.options.map((opt) => ({ text: opt.text, score: opt.score ?? 1 })),
      sort_order: item.sort_order || 0,
      is_enabled: item.is_enabled ?? 1,
    });
    setEditing(item);
    setCreating(false);
  };

  const updateOption = (index, field, value) => {
    setForm((prev) => {
      const options = [...prev.options];
      options[index] = { ...options[index], [field]: value };
      return { ...prev, options };
    });
  };

  const addOption = () => {
    setForm((prev) => ({
      ...prev,
      options: [...prev.options, { text: "", score: 1 }],
    }));
  };

  const removeOption = (index) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const saveQuestion = async (e) => {
    e.preventDefault();
    if (!form.question_text.trim()) {
      alert("请填写题目内容");
      return;
    }
    if (form.options.length < 2 || form.options.some((opt) => !opt.text.trim())) {
      alert("每道题至少需要 2 个有效选项");
      return;
    }

    const payload = {
      question_text: form.question_text.trim(),
      options: form.options.map((opt) => ({
        text: opt.text.trim(),
        score: Number(opt.score) || 1,
      })),
      sort_order: Number(form.sort_order) || 0,
      is_enabled: Number(form.is_enabled) || 0,
    };

    const url = editing
      ? `/api/admin/questionnaire/questions/${editing.id}`
      : "/api/admin/questionnaire/questions";
    const method = editing ? "PATCH" : "POST";

    const res = await fetch(url, {
      method,
      headers: getAdminHeaders(true),
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.detail || "保存失败");
      return;
    }

    setCreating(false);
    setEditing(null);
    setForm(emptyQuestion);
    loadAll();
  };

  const toggleEnabled = async (item) => {
    const res = await fetch(`/api/admin/questionnaire/questions/${item.id}`, {
      method: "PATCH",
      headers: getAdminHeaders(true),
      body: JSON.stringify({ is_enabled: item.is_enabled ? 0 : 1 }),
    });
    if (!res.ok) {
      const data = await res.json();
      alert(data.detail || "操作失败");
      return;
    }
    loadAll();
  };

  const deleteQuestion = async (item) => {
    if (!window.confirm(`确定删除题目「${item.q}」吗？`)) return;
    const res = await fetch(`/api/admin/questionnaire/questions/${item.id}`, {
      method: "DELETE",
      headers: getAdminHeaders(),
    });
    if (!res.ok) {
      const data = await res.json();
      alert(data.detail || "删除失败");
      return;
    }
    loadAll();
  };

  if (loading) {
    return <div className="text-gray-500">加载中...</div>;
  }

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">AI 测评题目</h2>
        <p className="text-sm text-gray-500 mt-1">
          配置前台 AI 性格测评题目与选项分值，保存后前台立即生效
        </p>
      </div>

      <form onSubmit={saveSettings} className="bg-white rounded-2xl shadow p-6 space-y-4">
        <h3 className="font-bold text-gray-800">页面标题</h3>
        <input
          value={settingsForm.page_title}
          onChange={(e) => setSettingsForm((prev) => ({ ...prev, page_title: e.target.value }))}
          placeholder="AI 性格测评"
          className="w-full border rounded-xl px-4 py-3"
        />
        <textarea
          value={settingsForm.page_subtitle}
          onChange={(e) =>
            setSettingsForm((prev) => ({ ...prev, page_subtitle: e.target.value }))
          }
          placeholder="副标题说明"
          rows={2}
          className="w-full border rounded-xl px-4 py-3"
        />
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={savingSettings}
            className="px-5 py-3 rounded-xl bg-blue-600 text-white font-bold disabled:opacity-60"
          >
            {savingSettings ? "保存中..." : "保存页面设置"}
          </button>
          {message && <span className="text-green-600 text-sm">{message}</span>}
        </div>
      </form>

      <div className="bg-white rounded-2xl shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-800">题目列表（{items.length}）</h3>
          <button
            type="button"
            onClick={openCreate}
            className="px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-bold"
          >
            添加题目
          </button>
        </div>

        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.id}
              className="border rounded-xl p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                    排序 {item.sort_order}
                  </span>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      item.is_enabled ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {item.is_enabled ? "启用" : "停用"}
                  </span>
                </div>
                <p className="font-medium text-gray-900">{item.q}</p>
                <p className="text-sm text-gray-500 mt-2">
                  选项：
                  {item.options.map((opt) => `${opt.text}(${opt.score}分)`).join(" / ")}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => openEdit(item)}
                  className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-sm"
                >
                  编辑
                </button>
                <button
                  type="button"
                  onClick={() => toggleEnabled(item)}
                  className="px-3 py-2 rounded-lg bg-amber-50 text-amber-700 text-sm"
                >
                  {item.is_enabled ? "停用" : "启用"}
                </button>
                <button
                  type="button"
                  onClick={() => deleteQuestion(item)}
                  className="px-3 py-2 rounded-lg bg-red-50 text-red-600 text-sm"
                >
                  删除
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {(creating || editing) && (
        <form onSubmit={saveQuestion} className="bg-white rounded-2xl shadow p-6 space-y-4">
          <h3 className="font-bold text-gray-800">
            {editing ? "编辑题目" : "添加题目"}
          </h3>
          <input
            value={form.question_text}
            onChange={(e) => setForm((prev) => ({ ...prev, question_text: e.target.value }))}
            placeholder="题目内容"
            className="w-full border rounded-xl px-4 py-3"
          />
          <div className="grid grid-cols-2 gap-4">
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm((prev) => ({ ...prev, sort_order: e.target.value }))}
              placeholder="排序"
              className="border rounded-xl px-4 py-3"
            />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={Boolean(form.is_enabled)}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, is_enabled: e.target.checked ? 1 : 0 }))
                }
              />
              启用此题
            </label>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-medium text-gray-700">选项（至少 2 个）</p>
              <button
                type="button"
                onClick={addOption}
                className="text-sm text-blue-600 font-bold"
              >
                + 添加选项
              </button>
            </div>
            {form.options.map((opt, index) => (
              <div key={index} className="flex gap-2">
                <input
                  value={opt.text}
                  onChange={(e) => updateOption(index, "text", e.target.value)}
                  placeholder={`选项 ${index + 1}`}
                  className="flex-1 border rounded-xl px-4 py-2"
                />
                <input
                  type="number"
                  value={opt.score}
                  onChange={(e) => updateOption(index, "score", e.target.value)}
                  placeholder="分值"
                  className="w-24 border rounded-xl px-3 py-2"
                />
                {form.options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className="px-3 text-red-500"
                  >
                    删
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              className="px-5 py-3 rounded-xl bg-blue-600 text-white font-bold"
            >
              保存题目
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setEditing(null);
                setForm(emptyQuestion);
              }}
              className="px-5 py-3 rounded-xl bg-gray-100 text-gray-700"
            >
              取消
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
